import React, { createContext, useContext, useState, useEffect } from 'react';

const DataContext = createContext();

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

export const DataProvider = ({ children }) => {
  const [hosts, setHosts] = useState([]);
  const [events, setEvents] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [internships, setInternships] = useState([]); // New state for internships
  const [interviewRequests, setInterviewRequests] = useState([]);
  const [interviews, setInterviews] = useState([]);
  const [candidateEvaluations, setCandidateEvaluations] = useState([]);

  useEffect(() => {
    // Mock Hosts (Universities/Organizations)
    const mockHosts = [
      { 
        id: 'host1', 
        name: 'Tech University', 
        email: 'admin@techuni.edu', 
        type: 'University', 
        status: 'active',
        contactPerson: 'Dr. Alan Turing',
        phone: '+1 (555) 010-1010',
        logo: null 
      },
      { 
        id: 'host2', 
        name: 'City Innovation Hub', 
        email: 'contact@cityhub.org', 
        type: 'Organization', 
        status: 'active',
        contactPerson: 'Jane Jacobs',
        phone: '+1 (555) 020-2020',
        logo: null 
      }
    ];

    // Mock Events
    const mockEvent = {
      id: '1',
      hostId: 'host1',
      name: 'Tech Job Fair 2024',
      date: '2024-03-15',
      startTime: '09:00',
      endTime: '17:00',
      location: 'Convention Center, City',
      description: 'Annual technology job fair featuring top companies'
    };

    // Mock Companies
    const mockCompanies = [
      {
        id: '1',
        hostId: 'host1',
        name: 'TechCorp Inc.',
        industry: 'Technology',
        description: 'Leading software development company',
        boothNumber: 'A1',
        website: 'https://techcorp.com',
        logo: 'https://images.unsplash.com/photo-1549923746-c502d488b3ea?w=100&h=100&fit=crop&crop=center',
        status: 'registered'
      },
      {
        id: '2',
        hostId: 'host1',
        name: 'DataSoft Solutions',
        industry: 'Data Analytics',
        description: 'Data science and analytics specialists',
        boothNumber: 'B2',
        website: 'https://datasoft.com',
        logo: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=100&h=100&fit=crop&crop=center',
        status: 'registered'
      }
    ];

    // Mock Candidates
    const mockCandidates = [
      { id: '123', name: 'John Doe', email: 'john@example.com', field: 'Software Engineering', experience: '3-5 years', city: 'New York', gender: 'Male', profileComplete: true },
      { id: '124', name: 'Jane Smith', email: 'jane@example.com', field: 'Data Science', experience: '5-8 years', city: 'San Francisco', gender: 'Female', profileComplete: true },
      { id: '125', name: 'Mike Johnson', email: 'mike@example.com', field: 'Product Management', experience: '1-3 years', city: 'Chicago', gender: 'Male', profileComplete: true },
      { id: '126', name: 'Sarah Wilson', email: 'sarah@example.com', field: 'Design', experience: '3-5 years', city: 'London', gender: 'Female', profileComplete: true },
      { id: '127', name: 'David Brown', email: 'david@example.com', field: 'Software Engineering', experience: '8+ years', city: 'Seattle', gender: 'Male', profileComplete: true },
      { id: '128', name: 'Emily Davis', email: 'emily@example.com', field: 'Marketing', experience: '0-1 years', city: 'Austin', gender: 'Female', profileComplete: false },
      { id: '129', name: 'Alex Kumar', email: 'alex@example.com', field: 'Software Engineering', experience: '1-3 years', city: 'Toronto', gender: 'Male', profileComplete: true },
      { id: '130', name: 'Lisa Wang', email: 'lisa@example.com', field: 'Finance', experience: '5-8 years', city: 'Singapore', gender: 'Female', profileComplete: true }
    ];

    // Mock Jobs
    const mockJobs = [
      { id: '1', companyId: '1', title: 'Frontend Developer', department: 'Engineering', level: 'Mid', location: 'Remote', description: 'Develop modern web applications using React and TypeScript' },
      { id: '2', companyId: '1', title: 'Backend Developer', department: 'Engineering', level: 'Senior', location: 'New York', description: 'Build scalable APIs and microservices' },
      { id: '3', companyId: '2', title: 'Data Scientist', department: 'Analytics', level: 'Mid', location: 'San Francisco', description: 'Analyze complex datasets and build ML models' }
    ];

    // Mock Internships
    const mockInternships = [
      { id: '1', companyId: '1', title: 'Summer Software Intern', positions: 5, isPaid: true, skillsOffered: 'React, Agile Development, Team Collaboration', description: 'Join our team for a 3-month intensive internship building real products.' }
    ];

    // Mock Evaluations
    const mockEvaluations = [
      { id: '1', candidateId: '123', candidateName: 'John Doe', companyId: '1', companyName: 'TechCorp Inc.', ratings: { englishLevel: 4, appearance: 5, technicalSkills: 4, communicationSkills: 5 }, notes: 'Excellent candidate with strong technical skills and great communication. Very professional appearance.', timestamp: '2024-03-15T10:30:00Z', interviewPosition: 'Frontend Developer' }
    ];

    // Mock Interviews
    const mockInterviews = [
      { id: 'int1', candidateId: '123', companyId: '1', type: 'specific', jobTitle: 'Frontend Developer', interviewTime: '2024-03-15T10:00:00Z', status: 'completed' },
      { id: 'int2', candidateId: '124', companyId: '2', type: 'specific', jobTitle: 'Data Scientist', interviewTime: '2024-03-15T11:00:00Z', status: 'scheduled' },
      { id: 'int3', candidateId: '125', companyId: '1', type: 'general', interviewTime: '2024-03-15T14:30:00Z', status: 'completed' }
    ];

    setHosts(mockHosts);
    setEvents([mockEvent]);
    setCompanies(mockCompanies);
    setCandidates(mockCandidates);
    setJobs(mockJobs);
    setInternships(mockInternships);
    setCandidateEvaluations(mockEvaluations);
    setInterviews(mockInterviews);
  }, []);

  const addHost = (host) => {
    const newHost = { ...host, id: Date.now().toString(), status: 'active' };
    setHosts(prev => [...prev, newHost]);
    return newHost;
  };

  const updateHost = (id, updates) => {
    setHosts(prev => prev.map(host => host.id === id ? { ...host, ...updates } : host));
  };

  const deleteHost = (id) => {
    setHosts(prev => prev.filter(host => host.id !== id));
  };

  const addInterviewRequest = (request) => {
    const newRequest = {
      ...request,
      id: Date.now().toString(),
      status: 'pending',
      createdAt: new Date().toISOString()
    };
    setInterviewRequests(prev => [...prev, newRequest]);
  };

  const updateInterviewRequest = (requestId, updates) => {
    setInterviewRequests(prev => prev.map(req => 
      req.id === requestId ? { ...req, ...updates } : req
    ));

    if (updates.status === 'accepted' && updates.interviewTime) {
      const interview = {
        id: Date.now().toString(),
        requestId,
        ...updates,
        createdAt: new Date().toISOString()
      };
      setInterviews(prev => [...prev, interview]);
    }
  };

  const addCandidateEvaluation = (evaluation) => {
    const newEvaluation = {
      ...evaluation,
      id: Date.now().toString()
    };
    setCandidateEvaluations(prev => [...prev, newEvaluation]);
    return newEvaluation;
  };

  const addCompany = (company) => {
    const newCompany = { ...company, id: Date.now().toString() };
    setCompanies(prev => [...prev, newCompany]);
    return newCompany;
  };

  const addJob = (job) => {
    const newJob = { ...job, id: Date.now().toString() };
    setJobs(prev => [...prev, newJob]);
    return newJob;
  };

  const addInternship = (internship) => {
    const newInternship = { ...internship, id: Date.now().toString() };
    setInternships(prev => [...prev, newInternship]);
    return newInternship;
  };

  const addEvent = (event) => {
    const newEvent = { ...event, id: Date.now().toString() };
    setEvents(prev => [...prev, newEvent]);
    return newEvent;
  };

  const value = {
    hosts,
    events,
    companies,
    candidates,
    jobs,
    internships,
    interviewRequests,
    interviews,
    candidateEvaluations,
    addHost,
    updateHost,
    deleteHost,
    addInterviewRequest,
    updateInterviewRequest,
    addCandidateEvaluation,
    addCompany,
    addJob,
    addInternship,
    addEvent
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};